import { ChevronLeft, ChevronRight, LayoutDashboard, LogOut, Package, QrCode, ScanLine, User, Users, WifiOff } from 'lucide-react'
import { useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { TrcArrowLogo, UserManagementModal, UserProfileModal } from '@/components/ui'
import { useAuth } from '@/features/auth/AuthContext'
import { useOnline } from '@/hooks/useOnline'
import { cn } from '@/lib/cn'

const NAV = [
  { to: '/', label: 'Home', icon: QrCode, end: true },
  { to: '/assets', label: 'Assets', icon: Package, end: false },
  { to: '/scan', label: 'Scan Tag', icon: ScanLine, end: false },
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, end: false },
]

const ROLE_PILLS: Record<string, { bg: string; color: string }> = {
  SUPER_ADMIN: { bg: '#2e1065', color: '#d8b4fe' },
  PARTNER: { bg: '#1e3a8a', color: '#bfdbfe' },
  MANAGER: { bg: '#064e3b', color: '#6ee7b7' },
  ASSET_MANAGER: { bg: '#064e3b', color: '#6ee7b7' },
  SENIOR: { bg: '#78350f', color: '#fde68a' },
  AUDITOR: { bg: '#78350f', color: '#fde68a' },
  ASSOCIATE: { bg: '#1e293b', color: '#94a3b8' },
  USER: { bg: '#053736', color: '#10b981' },
  CLIENT: { bg: '#1e3a5f', color: '#93c5fd' },
  REGULATOR: { bg: '#881337', color: '#fecdd3' },
}

export default function AppLayout() {
  const online = useOnline()
  const { user, logout } = useAuth()
  const { pathname } = useLocation()
  const [collapsed, setCollapsed] = useState(false)
  const [profileModalOpen, setProfileModalOpen] = useState(false)
  const [userMgmtOpen, setUserMgmtOpen] = useState(false)
  const hideChrome = pathname.startsWith('/scan') || pathname.startsWith('/register')

  const primaryRole = user?.roles?.[0] ?? 'USER'
  const isAdmin = Boolean(
    user?.roles?.some((r) => ['ADMIN', 'SUPER_ADMIN'].includes(r)) ||
    user?.permissions?.includes('user:manage'),
  )
  const roleStyle = ROLE_PILLS[primaryRole] ?? ROLE_PILLS.USER

  const initials = user?.full_name
    ? user.full_name
        .split(' ')
        .map((n) => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : 'U'

  return (
    <div className="min-h-dvh bg-slate-50 text-slate-900 md:flex font-sans antialiased selection:bg-emerald-500 selection:text-white">
      {/* ── DESKTOP SIDEBAR ── */}
      <aside
        style={{
          width: collapsed ? '68px' : '256px',
        }}
        className="hidden shrink-0 transition-all duration-200 cubic-bezier(0.4, 0, 0.2, 1) md:flex md:flex-col z-30 bg-[#042d2c] border-r border-slate-800/80 shadow-2xl"
      >
        {/* Logo Area */}
        <div className="flex h-16 items-center gap-3 border-b border-white/10 bg-black/20 px-4">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white p-1.5 shadow-md">
            <TrcArrowLogo size={20} />
          </div>
          {!collapsed && (
            <div className="overflow-hidden">
              <p className="text-[14px] font-black tracking-tight text-white leading-tight">
                TRC Asset360
              </p>
              <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-400">
                Enterprise Tracking
              </p>
            </div>
          )}
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 space-y-1.5 p-3">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-semibold transition-all duration-150',
                  isActive
                    ? 'bg-emerald-500/15 text-emerald-300 font-bold shadow-sm border border-emerald-500/30'
                    : 'text-slate-300/80 hover:bg-white/5 hover:text-white',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon
                    className={cn(
                      'h-4 w-4 shrink-0 transition-colors',
                      isActive ? 'text-emerald-400' : 'text-slate-400',
                    )}
                    aria-hidden
                  />
                  {!collapsed && <span>{item.label}</span>}
                </>
              )}
            </NavLink>
          ))}

          {isAdmin && (
            <button
              type="button"
              onClick={() => setUserMgmtOpen(true)}
              className={cn(
                'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-semibold transition-all duration-150',
                'text-slate-300/80 hover:bg-white/5 hover:text-white cursor-pointer',
              )}
            >
              <Users className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
              {!collapsed && <span>Team & Users</span>}
            </button>
          )}
        </nav>

        {/* User Card & Collapse */}
        <div className="border-t border-white/10 p-3 space-y-2.5 bg-black/10">
          {!collapsed && (
            <button
              type="button"
              onClick={() => setProfileModalOpen(true)}
              className="w-full rounded-xl p-2.5 bg-white/5 border border-white/10 hover:bg-white/10 transition text-left cursor-pointer group"
            >
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-600 text-xs font-black text-white shadow-sm">
                  {initials}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] font-bold text-white truncate group-hover:text-emerald-300 transition-colors">
                    {user?.full_name}
                  </p>
                  <span
                    style={{ backgroundColor: roleStyle.bg, color: roleStyle.color }}
                    className="inline-block rounded-md px-1.5 py-0.2 text-[9.5px] font-bold uppercase tracking-wider"
                  >
                    {primaryRole}
                  </span>
                </div>
              </div>
            </button>
          )}

          <button
            onClick={() => void logout()}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-rose-500/10 border border-rose-500/20 px-3 py-2 text-[12px] font-bold text-rose-300 hover:bg-rose-500/20 transition cursor-pointer"
          >
            <LogOut className="h-3.5 w-3.5" aria-hidden />
            {!collapsed && <span>Sign out</span>}
          </button>

          <button
            onClick={() => setCollapsed((c) => !c)}
            className="flex w-full items-center justify-center rounded-lg py-1.5 text-[11px] font-bold text-slate-400 hover:bg-white/5 hover:text-white transition cursor-pointer"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>
      </aside>

      {/* ── MAIN CONTENT AREA ── */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Offline Banner */}
        {!online && (
          <div
            role="status"
            className="flex items-center justify-center gap-2 bg-amber-500/15 border-b border-amber-500/30 px-4 py-2 text-[12px] font-bold text-amber-800"
          >
            <WifiOff className="h-3.5 w-3.5" aria-hidden />
            Offline — work is saved locally on this device and will sync.
          </div>
        )}

        {/* Main Routed Content */}
        <main className={cn('mx-auto w-full max-w-5xl flex-1 p-3.5 sm:p-6 md:p-8', !hideChrome && 'pb-24 md:pb-8')}>
          <Outlet />
        </main>

        {/* ── MOBILE BOTTOM NAVIGATION (Ergonomic, modern, floating center scan) ── */}
        {!hideChrome && (
          <nav
            className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-around bg-white/95 border-t border-slate-200/90 shadow-[0_-4px_25px_rgba(0,0,0,0.06)] backdrop-blur-md pb-safe md:hidden px-2 pt-1"
          >
            {/* Home */}
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                cn(
                  'flex min-h-[52px] flex-1 flex-col items-center justify-center gap-0.5 text-[10.5px] font-bold transition-all',
                  isActive ? 'text-emerald-700' : 'text-slate-400 hover:text-slate-600',
                )
              }
            >
              <QrCode className="h-5 w-5" aria-hidden />
              <span>Home</span>
            </NavLink>

            {/* Assets */}
            <NavLink
              to="/assets"
              className={({ isActive }) =>
                cn(
                  'flex min-h-[52px] flex-1 flex-col items-center justify-center gap-0.5 text-[10.5px] font-bold transition-all',
                  isActive ? 'text-emerald-700' : 'text-slate-400 hover:text-slate-600',
                )
              }
            >
              <Package className="h-5 w-5" aria-hidden />
              <span>Assets</span>
            </NavLink>

            {/* Elevated Center Scan Button */}
            <NavLink
              to="/scan"
              className="relative -top-3.5 flex flex-col items-center justify-center group active:scale-95 transition-transform"
              aria-label="Quick Scan QR Code"
            >
              <div className="flex h-13 w-13 items-center justify-center rounded-full bg-gradient-to-tr from-[#053736] to-emerald-600 text-white shadow-[0_6px_20px_rgba(16,185,129,0.45)] ring-4 ring-white transition-all group-hover:scale-105 group-hover:shadow-[0_8px_24px_rgba(16,185,129,0.6)]">
                <ScanLine className="h-6 w-6 stroke-[2.2]" />
              </div>
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-800 mt-0.5">
                Scan
              </span>
            </NavLink>

            {/* Dashboard */}
            <NavLink
              to="/dashboard"
              className={({ isActive }) =>
                cn(
                  'flex min-h-[52px] flex-1 flex-col items-center justify-center gap-0.5 text-[10.5px] font-bold transition-all',
                  isActive ? 'text-emerald-700' : 'text-slate-400 hover:text-slate-600',
                )
              }
            >
              <LayoutDashboard className="h-5 w-5" aria-hidden />
              <span>Dashboard</span>
            </NavLink>

            {/* Profile */}
            <button
              type="button"
              onClick={() => setProfileModalOpen(true)}
              className="flex min-h-[52px] flex-1 flex-col items-center justify-center gap-0.5 text-[10.5px] font-bold text-slate-400 hover:text-slate-600 transition-all active:scale-95 cursor-pointer"
            >
              <User className="h-5 w-5" aria-hidden />
              <span>Profile</span>
            </button>
          </nav>
        )}
      </div>

      {/* Global Profile & Logout Modal */}
      <UserProfileModal open={profileModalOpen} onClose={() => setProfileModalOpen(false)} />

      {/* Global Team & User Management Modal */}
      <UserManagementModal open={userMgmtOpen} onClose={() => setUserMgmtOpen(false)} />
    </div>
  )
}
